// A small menu opened where the pointer was (right-click on a room row
// or a section header). Plain buttons, like MoreMenu: no `role="menu"`
// because arrow-key roving is not implemented.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";

export interface PointerMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
}

const MENU_WIDTH = 180;
const EDGE = 8;

export function PointerMenu({
  x,
  y,
  label,
  items,
  onClose,
}: {
  x: number;
  y: number;
  label: string;
  items: PointerMenuItem[];
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const portalRoot = usePortalRoot();

  useEffect(() => {
    menuRef.current?.querySelector<HTMLButtonElement>("button:not([disabled])")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [onClose]);

  if (!portalRoot) return null;
  const left = Math.max(EDGE, Math.min(x, window.innerWidth - MENU_WIDTH - EDGE));

  return createPortal(
    <div
      ref={menuRef}
      aria-label={label}
      style={{
        position: "fixed",
        top: y,
        left,
        zIndex: 50,
        width: MENU_WIDTH,
        padding: "var(--chat-space-1) 0",
        borderRadius: "var(--chat-radius-xl)",
        border: "1px solid var(--chat-surface-border)",
        background: "var(--chat-surface-overlay)",
        boxShadow: "var(--chat-shadow-popover)",
      }}
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          className="chat-button"
          disabled={item.disabled}
          onClick={() => {
            item.onSelect();
            onClose();
          }}
          style={{
            display: "block",
            width: "100%",
            textAlign: "left",
            padding: "var(--chat-space-2) var(--chat-space-3)",
            border: "none",
            background: "transparent",
            color: item.disabled ? "var(--chat-fg-muted)" : "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-sm-size)",
            lineHeight: "var(--chat-type-body-sm-line)",
            cursor: item.disabled ? "default" : "pointer",
          }}
        >
          {item.label}
        </button>
      ))}
    </div>,
    portalRoot,
  );
}
